import { Fragment, memo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { Checkbox, FormRow, Label, QueryDropdown, WithTooltip } from 'basic-components';
import { Info } from 'basic-components/icons';
import { ReduxService } from 'dashboard-services/services';

import useValidatorTaskModule from '../../../hooks/useValidatorTask';
import ToggleRow from '../../ToggleRow';
import TaskParam from './TaskParam';
import WarningMessage from './WarningMessage';

import { When } from 'react-if';

import './DynamicValidation.scss';

const { useTaskInput } = useValidatorTaskModule

const DynamicValidation = ({ continueIfTaskNotFound, errors, menuPortalTarget, onChangeValidationOptions, setDynamicValidation, shouldRemoveInvalidParams, validatorTask }) => {
  const {
          taskName,
          taskVersion,
          tasks,
          defaultTask,
          fetchMoreTasks,
          isFetchingTasks,
          setTaskQuery,
          versions,
          defaultTaskVersion,
          fetchMoreVersions,
          isFetchingVersions,
          setVersionsQuery,
          getAllParams,
          paramsCleanEntries,
          invalidParamNames,
          areAllInvalidParamsFromDVFile
        } = validatorTask,
        taskRef = useRef(),
        { onChangeTaskInput, onChangeTaskVersion, onChangeParamValue } = useTaskInput({ setDynamicValidation }),
        onChangeDynamicValidation = useCallback((value, { name }) => setDynamicValidation(dynamicValidation => ReduxService.updateObject(dynamicValidation, { [name]: value })), [setDynamicValidation])

  useEffect(() => {
    errors.some(error => error.field === "taskName") && taskRef.current?.validateForForm()
  }, [errors])

  return (
    <div className="data-lake__group-dialog__advanced__dynamic-validation">
      <ToggleRow
          label="Continue on missing task"
          name="continueIfTaskNotFound"
          on={!!continueIfTaskNotFound}
          onChange={onChangeDynamicValidation}
      />
      <FormRow isVertical>
        <Label required>
          <div className="data-lake__group-dialog__label-with-info">
            Validator task
            <WithTooltip text="Task used to validate new files and updates uploaded to this group">
              <div className="data-lake__group-dialog__label-with-info__tooltip">
                <Info
                    color="blue-bright"
                    height={16}
                    width={16}
                />
              </div>
            </WithTooltip>
          </div>
        </Label>
        <QueryDropdown
            defaultValue={defaultTask}
            fetchNextPage={fetchMoreTasks}
            isFetching={isFetchingTasks}
            menuPortalTarget={menuPortalTarget}
            name="taskName"
            onChange={onChangeTaskInput}
            onInputChange={setTaskQuery}
            options={tasks}
            placeholder="Select task"
            ref={taskRef}
            required
            variant="border"
        />
      </FormRow>
      <When condition={!!taskName}>
        {() => (
          <Fragment>
            <FormRow className="data-lake__group-dialog__advanced__dynamic-validation__row data-lake__group-dialog__advanced__dynamic-validation__row--with-indent">
              <Label>Validator task version</Label>
              <QueryDropdown
                  defaultValue={defaultTaskVersion}
                  fetchNextPage={fetchMoreVersions}
                  isFetching={isFetchingVersions}
                  menuPortalTarget={menuPortalTarget}
                  name="taskVersion"
                  noError
                  onChange={onChangeTaskVersion}
                  onInputChange={setVersionsQuery}
                  options={versions}
                  placeholder="Select version"
                  required
                  variant="line"
              />
            </FormRow>
            <When condition={!!taskVersion && !!defaultTaskVersion?.task}>
              {() => (
                <Fragment>
                  {paramsCleanEntries.map(([paramName, paramValue]) => (
                    <TaskParam
                        getAllParams={getAllParams}
                        key={paramName}
                        menuPortalTarget={menuPortalTarget}
                        onChangeParamValue={onChangeParamValue}
                        paramDef={defaultTaskVersion.task.params.find(p => p.name === paramName)}
                        paramName={paramName}
                        paramValue={paramValue}
                        taskName={defaultTaskVersion.task.name}
                    />
                  ))}
                  <When condition={invalidParamNames.length > 0 && areAllInvalidParamsFromDVFile}>
                    {() => (
                      <Fragment>
                        <WarningMessage>
                          The following {invalidParamNames.length > 1 ? "parameters are" : "parameter is"} not available in the selected task version: {invalidParamNames.join(", ")}
                        </WarningMessage>
                        <FormRow className="data-lake__group-dialog__advanced__dynamic-validation__row--with-checkbox">
                          <Checkbox
                              checked={shouldRemoveInvalidParams}
                              name="shouldRemoveInvalidParams"
                              noError
                              onChange={onChangeValidationOptions}
                          >
                            Remove invalid {invalidParamNames.length > 1 ? "parameters" : "parameter"} from configuration
                          </Checkbox>
                        </FormRow>
                      </Fragment>
                    )}
                  </When>
                  <When condition={invalidParamNames.length > 0 && !areAllInvalidParamsFromDVFile}>
                    {() => (
                      <WarningMessage>
                        Found duplicated task version, issues expected.
                      </WarningMessage>
                    )}
                  </When>
                </Fragment>
              )}
            </When>
          </Fragment>
        )}
      </When>
    </div>
  )
}

DynamicValidation.propTypes = {
  continueIfTaskNotFound: PropTypes.bool,
  errors: PropTypes.array.isRequired,
  menuPortalTarget: PropTypes.object,
  onChangeValidationOptions: PropTypes.func.isRequired,
  setDynamicValidation: PropTypes.func.isRequired,
  shouldRemoveInvalidParams: PropTypes.bool.isRequired,
  validatorTask: PropTypes.object.isRequired
}

export default memo(DynamicValidation)
